/*
 * Copyright (c) 2016 Omnigon Communications, LLC. All rights reserved.
 *
 * This software is the confidential and proprietary information of Omnigon Communications, LLC
 * ("Confidential Information"). You shall not disclose such Confidential Information and shall access and use it only
 * in accordance with the terms of the license agreement you entered into with Omnigon Communications, LLC, its
 * subsidiaries, affiliates or authorized licensee. Unless required by applicable law or agreed to in writing, this
 * Confidential Information is provided on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 * express or implied. See the license agreement for the specific language governing permissions and limitations.
 */

import _ from 'lodash';
import can from 'can';
import Component from 'can/component/component';
import FeedService from 'scripts/services/base-feed-service';
import template from './player-statistics.stache';
import dtm from 'scripts/services/dtm';

const statisticsFeedUrl = window.globalSettings.feeds.smt.playerStatisticsUrl;
const courseStatisticsPageUrl =
    window.globalSettings.pagePaths.courseStatistics;
const playerComparisonPageUrl =
    window.globalSettings.pagePaths.playerComparison;
const statisticsSponsor =
    window.globalSettings.statisticsSettings.playerStatsSponsor;

const strokesGained = {
    title: 'Strokes Gained',
    value: 'strokes-gained',
};

const winProbability = {
    title: 'Win Probability',
    value: 'win-probability',
};

const missedFairwayPenalty = {
    title: 'Missed Fairway Penalty',
    value: 'missed-fairway-penalty',
};

// filter titles can be changed
const statsFilter = [
    { title: 'Driving Distance', value: 'driving-distance' },
    { title: 'Fairways Hit', value: 'fairways-hit' },
    { title: 'Greens in Regulation', value: 'greens-in-regulation' },
    { title: 'Putting', value: 'putting' },
    { title: 'Sand Saves', value: 'sand-saves' },
    { title: 'Birdies', value: 'birdies' },
    { title: 'Eagles', value: 'eagles' },
];

const defaultRoundFilter = { title: 'All Rounds', value: 'all' };
const totalFilter = { title: 'Total', value: 'all' };

const roundFilters = [
    { title: 'Round 1', value: '1' },
    { title: 'Round 2', value: '2' },
    { title: 'Round 3', value: '3' },
    { title: 'Round 4', value: '4' },
];

const statFilters = [
    { title: 'total', value: 'total' },
    { title: 'off the tee', value: 'off-the-tee' },
    { title: 'approach', value: 'approach' },
    { title: 'around the green', value: 'short-game' },
    { title: 'putting', value: 'strokes-putting' },
];

const makeCutColumn = { title: 'Make Cut', value: 'make-cut' };
const topTenColumn = { title: 'Top Ten', value: 'top-ten' };
const winColumn = { title: 'Win', value: 'win' };

const years = [
    { title: 2018, value: 2018 },
    { title: 2017, value: 2017 },
];

// kpi titles are stored on the backend part and always the same
const kpiMapping = new can.Map({
    total: 'Total',
    'off-the-tee': 'Strokes Gained Off the Tee',
    approach: 'Strokes Gained Approach',
    'short-game': 'Strokes Gained Short Game',
    'strokes-putting': 'Strokes Gained Putting',
    'driving-distance': 'Driving Distance',
    'fairways-hit': 'Fairways Hit',
    'greens-in-regulation': 'Greens in Regulation',
    putting: 'Number of Putts',
    'sand-saves': 'Sand Saves',
    birdies: 'Birdies',
    eagles: 'Eagles',
    [makeCutColumn.value]: 'Make Cut',
    [topTenColumn.value]: 'Top Ten',
    [winColumn.value]: 'Win',
    [missedFairwayPenalty.value]: 'Missed Fairway',
});

const percentageRequired = [
    'fairways-hit',
    'greens-in-regulation',
    'sand-saves',
];

const priority = ['CUT', 'MC', 'WD', 'DQ'];

Component.extend({
    tag: 'player-statistics',
    template: template,
    leakScope: false,
    viewModel: {
        statTabs: [
            strokesGained,
            ...statsFilter,
            winProbability,
            missedFairwayPenalty,
        ],
        roundTabs: [defaultRoundFilter, ...roundFilters],
        roundFilters,
        years: years,
        define: {
            fetching: {
                type: 'boolean',
                value: true,
            },
            notWebView: {
                get() {
                    return !_.get(window.globalSettings, 'inWebView');
                },
            },
            preLegend: {
                get() {
                    if (this.attr('statFilter') !== winProbability.value) {
                        return '';
                    }

                    if (this.attr('beforeSecondRound')) {
                        return ' | Denotes Pre-Championship Probability';
                    }

                    return ' | Denotes Pre-Round Probability';
                },
            },
            preLegendTooltip: {
                get() {
                    if (this.attr('statFilter') !== winProbability.value) {
                        return '';
                    }

                    if (this.attr('beforeSecondRound')) {
                        return 'Probabilities are calculated based on a predictive model using previous tournament scores, player performance, and course/hole difficulty. It is only an indicator of the likelihood of player performance';
                    }

                    return 'Probabilities are calculated based on a predictive model using previous tournament scores, player performance, course/hole difficulty and remaining holes. It is only an indicator of the likelihood of player performance';
                },
            },
            beforeSecondRound: {
                get() {
                    return Number(this.attr('roundFilter')) < 2;
                },
            },
            statFilter: {
                type: 'string',
                value: strokesGained.value,
            },
            roundFilter: {
                type: 'string',
                value: defaultRoundFilter.value,
            },
            tableHeaders: {
                get: function() {
                    if (this.isStrokesGainedActive()) {
                        return statFilters;
                    }

                    if (this.isWinProbabilityActive()) {
                        return Number(this.attr('roundFilter')) > 2
                            ? [topTenColumn, winColumn]
                            : [makeCutColumn, topTenColumn, winColumn];
                    }

                    if (this.isMissedFairwayActive()) {
                        return [...roundFilters, totalFilter];
                    }

                    return [...roundFilters, defaultRoundFilter];
                },
            },
            tableValues: {
                get() {
                    return this.getFilteredStatistics();
                },
            },
            sortKey: {
                type: 'string',
                value: 'rank',
            },
            sortAsc: {
                type: 'boolean',
                value: true,
            },
            playerStats: {
                Type: can.List,
            },
            courseStatisticsPageUrl: {
                type: 'string',
                value: courseStatisticsPageUrl,
            },
            playerComparisonPageUrl: {
                get() {
                    const urlOptions = {
                        player1: this.attr('firstPlayerId') || '',
                        player2: this.attr('secondPlayerId') || '',
                    };
                    return can.sub(playerComparisonPageUrl, urlOptions);
                },
            },
            sponsor: {
                type: can.Map,
                value: statisticsSponsor,
            },
            dataNotAvailable: {
                type: 'boolean',
                value: false,
            },
        },

        isStrokesGainedActive() {
            return this.attr('statFilter') === strokesGained.value;
        },

        isWinProbabilityActive() {
            return this.attr('statFilter') === winProbability.value;
        },

        isMissedFairwayActive() {
            return this.attr('statFilter') === missedFairwayPenalty.value;
        },

        getCurrentFilter() {
            if (this.isStrokesGainedActive()) {
                const roundFilter = this.attr('roundFilter');
                const availableFilters = [
                    defaultRoundFilter,
                    ...roundFilters,
                ].map((it) => it.value);

                return availableFilters.includes(roundFilter)
                    ? roundFilter
                    : defaultRoundFilter.value;
            }

            if (this.isWinProbabilityActive()) {
                const filter = this.attr('roundFilter');
                const routeFilter = can.route.attr(winProbability.value);
                const availableFilters = roundFilters.map((it) => it.value);

                if (
                    filter &&
                    filter !== 'all' &&
                    availableFilters.includes(filter)
                ) {
                    return filter;
                }

                if (routeFilter) {
                    return routeFilter;
                }

                return roundFilters[0].value;
            }

            return this.attr('statFilter');
        },

        getStatKey(valueKey) {
            if (this.isStrokesGainedActive() || this.isWinProbabilityActive()) {
                return `stats.${valueKey}.${this.roundFilter}`;
            }

            return `stats.${this.statFilter}.all`;
        },

        getTotalStatKey() {
            return this.getStatKey('total');
        },

        getRankSortingField() {
            if (this.isWinProbabilityActive()) {
                if (window.innerWidth < 640) {
                    return `stats.win.${this.roundFilter}.rankOrder`;
                }

                return this.roundFilter > 2
                    ? `stats.win.${this.roundFilter}.rankOrder`
                    : `stats.make-cut.${this.roundFilter}.rankOrder`;
            }

            const totalStatKey = this.getTotalStatKey();
            return `${totalStatKey}.rankOrder`;
        },

        getRankKey() {
            if (this.isWinProbabilityActive()) {
                if (window.innerWidth < 640) {
                    return `stats.win.${this.roundFilter}.rankOrder`;
                }

                return this.roundFilter > 2
                    ? `stats.win.${this.roundFilter}.rank`
                    : `stats.make-cut.${this.roundFilter}.rank`;
            }

            const sortKey = this.attr('sortKey');
            const defaultRankKeys = ['rank', 'pos', 'player'];
            if (defaultRankKeys.includes(sortKey)) {
                const totalStatKey = this.getTotalStatKey();
                return `${totalStatKey}.rank`;
            }

            const statKey = this.getStatKey(sortKey);
            return `${statKey}.rank`;
        },

        getRank(stats) {
            const statKey = this.getRankKey();
            const rank = stats.attr(statKey) || '-';
            if (rank === undefined || rank === '') {
                return '-';
            }
            return rank;
        },

        calculatePercentage(count, total) {
            let result = 0;
            if (total) {
                result = (count / total) * 100;
            }
            return result.toFixed(2);
        },

        getDisplayValue(stat, defaultValue) {
            const defaultDisplayValue = {
                value: defaultValue,
                percentage: '',
                color: stat ? stat.color : '',
            };

            if (!stat) {
                return defaultDisplayValue;
            }

            const displayValue = stat.attr('stat.displayValue');
            if (!displayValue) {
                return defaultDisplayValue;
            }

            const isPercentageRequired = percentageRequired.includes(
                this.statFilter,
            );

            if (isPercentageRequired) {
                const absoluteValue = [stat.count, stat.total].join(' / ');
                const percentageValue = this.calculatePercentage(
                    stat.count,
                    stat.total,
                );

                return {
                    value: absoluteValue,
                    percentage: `(${percentageValue}%)`,
                    color: stat.color,
                };
            }

            return {
                value: displayValue,
                percentage: '',
                color: stat.color,
            };
        },

        getStatField({ header, statFilter, roundFilter }) {
            const statKey =
                this.isStrokesGainedActive() || this.isWinProbabilityActive()
                    ? header
                    : statFilter;
            const roundKey =
                this.isStrokesGainedActive() || this.isWinProbabilityActive()
                    ? roundFilter
                    : header;
            return `stats.${statKey}.${roundKey}`;
        },
        getFilteredStatistics() {
            const headerFilters = this.attr('tableHeaders');
            const playerData = this.attr('playerStats');

            // TODO: Investigate why using reduce decrease performance
            return playerData
                .map((playerDataEntry) => {
                    const displayStats = headerFilters.map((header) => {
                        const statField = this.getStatField({
                            header: header.value,
                            statFilter: this.statFilter,
                            roundFilter: this.roundFilter,
                        });
                        const stat = playerDataEntry.attr(statField);
                        const defaultValue = this.isStrokesGainedActive()
                            ? 0
                            : '-';
                        return this.getDisplayValue(stat, defaultValue);
                    });

                    return {
                        rank: this.getRank(playerDataEntry),
                        position: playerDataEntry.position.displayValue,
                        player: playerDataEntry.player,
                        stats: displayStats,
                        status: playerDataEntry.status,
                    };
                })
                .filter((player) => {
                    const shouldCheckRound =
                        this.isStrokesGainedActive() ||
                        this.isWinProbabilityActive();

                    if (
                        shouldCheckRound &&
                        (this.roundFilter === 'all' || this.roundFilter <= 2)
                    ) {
                        return true;
                    }

                    return !player.status || player.status !== 'Cut';
                });
        },

        getCellColor(roundInfo) {
            if (roundInfo.preProbability) {
                return '#067BCA';
            }
        },

        flatStrokesStats(statData) {
            return statData.reduce((acc, roundInfo) => {
                if (
                    [strokesGained.title, winProbability.title].includes(
                        roundInfo.name,
                    )
                ) {
                    roundInfo.sections.forEach((section) => {
                        const color = this.getCellColor(roundInfo);

                        acc.push(Object.assign(section, { color }));
                    });
                    return acc;
                }

                acc.push(roundInfo);
                return acc;
            }, []);
        },

        createSortingStatField: function(round) {
            return `${this.getStatField({
                header: round,
                statFilter: this.statFilter,
                roundFilter: round,
            })}.stat.value`;
        },

        getSortResult(aValue, bValue, sortAsc) {
            let res = 0;
            res = Number(aValue) - Number(bValue);
            if (Number.isNaN(res)) {
                res = String(aValue).localeCompare(String(bValue));
            }

            return sortAsc ? res : res * -1;
        },
        getSortingField(sortKey) {
            switch (sortKey) {
                case 'rank':
                    return this.getRankSortingField();
                case 'pos':
                    return 'position.value';
                case 'player':
                    return 'player.lastName';
                case '1':
                    return this.createSortingStatField('1');
                case '2':
                    return this.createSortingStatField('2');
                case '3':
                    return this.createSortingStatField('3');
                case '4':
                    return this.createSortingStatField('4');
                default:
                    if (
                        this.isStrokesGainedActive() ||
                        this.isWinProbabilityActive()
                    ) {
                        const statKey = this.getStatKey(sortKey);
                        return `${statKey}.rankOrder`;
                    }
                    return null;
            }
        },

        getSortingFunc(sortKey, sortAsc) {
            const sortingField = this.getSortingField(sortKey);
            return (a, b) => {
                let aValue = a.attr(sortingField) || '-';
                let bValue = b.attr(sortingField) || '-';

                if (aValue === '-') {
                    return 1;
                }
                if (bValue === '-') {
                    return -1;
                }

                // specific logic for position field
                if (sortingField === 'pos') {
                    const aPos = aValue.replace(/D/g, '');
                    const bPos = bValue.replace(/D/g, '');

                    if (!aPos) {
                        aValue = priority.indexOf(aValue) + 999;
                    } else {
                        aValue = aPos;
                    }
                    if (!bPos) {
                        bValue = priority.indexOf(bValue) + 999;
                    } else {
                        bValue = bPos;
                    }
                }

                // additional sorting by ranks on equal round values
                if (aValue === bValue) {
                    const aStatsValue = a.attr(this.getTotalStatKey());
                    const bStatsValue = b.attr(this.getTotalStatKey());
                    aValue = aStatsValue ? aStatsValue.attr('rankOrder') : 0;
                    bValue = bStatsValue ? bStatsValue.attr('rankOrder') : 0;
                }

                return this.getSortResult(aValue, bValue, sortAsc);
            };
        },

        updateComparator() {
            const playerStats = this.attr('playerStats');
            if (playerStats) {
                can.batch.start();
                playerStats.attr(
                    'comparator',
                    this.getSortingFunc(
                        this.attr('sortKey'),
                        this.attr('sortAsc'),
                    ),
                );
                can.batch.stop();
            }
        },
    },

    events: {
        inserted() {
            this.initFeedService();
            this.registerRoutes();
            this.registerModelListeners();

            this.getHashRoute();
        },

        getHashRoute() {
            if (can.route.attr(strokesGained.value)) {
                this.viewModel.attr('statFilter', strokesGained.value);
                const strokesGainedFilter = can.route.attr(strokesGained.value);
                this.viewModel.attr('roundFilter', strokesGainedFilter);
                return strokesGainedFilter;
            }

            if (can.route.attr(winProbability.value)) {
                this.viewModel.attr('statFilter', winProbability.value);
                const winProbabilityFilter = can.route.attr(
                    winProbability.value,
                );
                this.viewModel.attr('roundFilter', winProbabilityFilter);
                return winProbabilityFilter;
            }

            if (can.route.attr('kpi')) {
                const activeFilter = can.route.attr('kpi');
                this.viewModel.attr('statFilter', activeFilter);
                return activeFilter;
            }

            this.initRoute();
            return defaultRoundFilter.value;
        },

        initRoute() {
            this.viewModel.attr('statFilter', strokesGained.value);
            const roundFilter = defaultRoundFilter.value;
            this.viewModel.attr('roundFilter', roundFilter);
            this.doRoute({ [strokesGained.value]: roundFilter });
        },

        registerRoutes() {
            can.route('strokes-gained=:category', {});
            can.route('win-probability=:category', {});
            can.route(':kpi', {});
            can.route.ready();
        },

        doRoute(routeAttr) {
            can.route.attr(routeAttr, true);
            this.viewModel.updateComparator();
        },

        getRouteAttr() {
            if (this.viewModel.isStrokesGainedActive()) {
                return strokesGained.value;
            }

            if (this.viewModel.isWinProbabilityActive()) {
                return winProbability.value;
            }

            return 'kpi';
        },

        registerModelListeners() {
            const getFilterRoute = () => {
                const routeAttr = this.getRouteAttr();
                const routeValue = this.viewModel.getCurrentFilter();
                return { [routeAttr]: routeValue };
            };
            this.viewModel.bind('statFilter', () => {
                if (
                    this.viewModel.attr('roundFilter') ===
                    this.viewModel.getCurrentFilter()
                ) {
                    this.trackStatsTab();
                }

                this.viewModel.attr(
                    'roundFilter',
                    this.viewModel.getCurrentFilter(),
                );

                this.doRoute(getFilterRoute());
            });
            this.viewModel.bind('roundFilter', () => {
                this.trackStatsTab();

                this.doRoute(getFilterRoute());
            });
            this.viewModel.bind('sortKey', () => {
                this.viewModel.updateComparator();
            });
            this.viewModel.bind('sortAsc', () => {
                this.viewModel.updateComparator();
            });

            window.addEventListener('resize', () => {
                this.viewModel.updateComparator();
            });
        },

        trackStatsTab() {
            const tab = this.viewModel.attr('statFilter');
            const round = this.viewModel.attr('roundFilter');

            const clickedTab = _.find(
                this.viewModel.attr('statTabs'),
                (it) => it.value === tab,
            );
            const clickedRound = _.find(
                this.viewModel.attr('roundTabs'),
                (it) => it.value === round,
            );
            dtm.trackPlayerStatsTab(
                'playerStatsTab',
                clickedTab ? _.capitalize(clickedTab.title) : undefined,
                round === tab ? null : _.capitalize(clickedRound.title),
            );
        },

        initFeedService() {
            this.statsFeedService = new FeedService({
                feedUrl: statisticsFeedUrl,
                fromCache: false,
            });

            this.statsFeedService
                .on('fetching', (ev, value) =>
                    this.viewModel.attr('fetching', value),
                )
                .on('data', (ev, data) => {
                    data = data || [];
                    const playerStats = !this.viewModel.historyMode
                        ? this.getPlayerStats(data)
                        : this.getPlayerHistoryStats(data);

                    can.batch.start();
                    this.viewModel.attr('playerStats', playerStats);
                    this.viewModel.updateComparator();
                    can.batch.stop();
                })
                .on('error', () =>
                    this.viewModel.attr('dataNotAvailable', true),
                );
            this.statsFeedService.fetch();
        },

        getPlayerStats(data) {
            return new can.List(
                data.map((statsRow) => {
                    const result = {
                        player: _.get(statsRow, 'standing.player'),
                        position: _.get(statsRow, 'standing.position'),
                        status: _.get(statsRow, 'standing.status'),
                        stats: {},
                    };

                    kpiMapping.each((kpiTitle, key) => {
                        result.stats[key] = {};
                        for (let i = 0; i < 4; i++) {
                            const roundInfo = _.get(
                                statsRow,
                                `roundsInfo.${i}.stats`,
                                [],
                            );
                            const flatRoundStats = this.viewModel.flatStrokesStats(
                                roundInfo,
                            );
                            const roundStats =
                                flatRoundStats
                                    .filter((stat) => stat.name === kpiTitle)
                                    .pop() || null;
                            _.set(result, `stats.${key}.${i + 1}`, roundStats);
                        }
                        const allRoundsInfo = _.get(
                            statsRow,
                            'overallStats',
                            [],
                        );
                        const flatAllRoundsStats = this.viewModel.flatStrokesStats(
                            allRoundsInfo,
                        );
                        const allRoundsStats =
                            flatAllRoundsStats
                                .filter((stat) => stat.name === kpiTitle)
                                .pop() || null;
                        _.set(result, `stats.${key}.all`, allRoundsStats);
                    });
                    return result;
                }),
            );
        },

        getPlayerHistoryStats(data) {
            // History data retrieving will be here
            return this.getPlayerStats(data);
        },

        destroyFeedService() {
            if (this.statsFeedService) {
                this.statsFeedService.destroy();
                this.statsFeedService = null;
            }
        },

        removed() {
            this.destroyFeedService();
        },
    },
});
